import React, { useState, useEffect } from 'react';
import { css } from '@emotion/css';
import { Subscribe } from "unstated";
import { color_shades_lighter, color_shades_dark } from '../../constants/colors';
import TemplateFormFieldValueModel from '../../models/templating/api/TemplateFormFieldValueModel';
import TemplateFormFieldModel from '../../models/templating/api/TemplateFormFieldModel';
import TemplateFormSectionModel from '../../models/templating/api/TemplateFormSectionModel';
import CheckboxField from '../../components/forms/CheckboxField';
import TextField from '../../components/forms/TextField';
import TextAreaField from '../../components/forms/TextAreaField';
import SelectField from '../../components/forms/SelectField';
import Switch from '../../components/forms/Switch';
import FormFieldCard from '../../components/forms/FormFieldCard';
import PanelContainer from '../../components/structure/Panels/PanelContainer';
import TemplateInstructionsPanel from '../applicationAppliedTemplateManager/components/TemplateInstructionsPanel';
import ScrollablePanelBody from '../../components/structure/ScrollablePanelBody';
import RadioGroup from '../../components/forms/RadioGroup';
import CheckboxGroup from '../../components/forms/CheckboxGroup';
import SsmlField from '../../components/forms/SsmlField/SsmlField';
import FileUploadField from '../../components/forms/FileUploadField';
import MediaContainer from '../../state/containers/MediaContainer';
import CustomScrollbars from '../../components/structure/CustomScrollbars';
import SparkContainer from '../../hooks/SparkContainer';
import { getVisibleFields } from '../../utils/sectionUtils';
import TimeRangeField from '../../components/forms/TimeRangeField';
import SingleDayTimeRangeField from '../../components/forms/SingleDayTimeRangeField';
import SpecialDateField from '../../components/forms/SpecialDateField';
import ApplicationContainer from '../../state/containers/ApplicationContainer';
import AppliedApplicationTemplateModel from '../../models/templating/api/AppliedApplicationTemplateModel';
import { isDeprecatedSingleDayTimeRangeField, isDeprecatedSpecialDateField } from '../../utils/fieldTypeUtils';
import { evaluateConditionalDefault } from '../../utils/conditionalDefaultUtils';
import { splitOvernightValue, mergeOvernightPrefix, extractOvernightPrefix, reconstructOvernightDisplay } from '../../utils/weeklyScheduleFormatters';
import ApplicationVariablesContainer from '../../hooks/ApplicationVariablesContainer';
import OrganizationVariablesContainer from '../../hooks/OrganizationVariablesContainer';
import SaveFieldVariationModal from '../../components/forms/SaveFieldVariationModal';
import ViewFieldVariationsModal from '../../components/forms/ViewFieldVariationsModal';
import ApplicationVariable from '../../models/applications/ApplicationVariable';
import PhoneNumberFormField from '../../components/forms/PhoneNumberFormField';
import { getFieldVariations as filterFieldVariations } from '../../utils/fieldVariations';

interface AppliedApplicationTemplateFormProps {
    section: TemplateFormSectionModel
    onFieldValueChange: (field: TemplateFormFieldModel, value: string) => void
    /** Atomic multi-field update — applies all updates in one setFieldValue call to avoid stale-closure races. */
    onMultipleFieldValuesChange?: (updates: Array<{ field: TemplateFormFieldModel, value: string }>) => void
    applicationId: string
    organizationId?: string
    appContainer: ApplicationContainer
    values?: TemplateFormFieldValueModel[]
    fieldsOnly?: boolean
    preview?: boolean
    recentlyAddedModuleFormFields?: TemplateFormFieldModel[]
    showAdminFields?: boolean
    appliedAppTemplate?: AppliedApplicationTemplateModel
    onFieldValidationChange?: (fieldId: string, hasErrors: boolean) => void
    /** All fields across all sections in the form, used to evaluate cross-section conditional defaults. */
    allFormFields?: TemplateFormFieldModel[]
}

const AppliedApplicationTemplateSectionForm: React.FC<AppliedApplicationTemplateFormProps> = (props) => {
    const sparkContainer = SparkContainer.useContainer();
    const variablesContainer = ApplicationVariablesContainer.useContainer();
    const organizationVariablesContainer = OrganizationVariablesContainer.useContainer();

    const [saveVariationModalVisible, setSaveVariationModalVisible] = useState(false);
    const [viewVariationsModalVisible, setViewVariationsModalVisible] = useState(false);
    const [selectedField, setSelectedField] = useState<TemplateFormFieldModel | null>(null);

    // Load application variables when component mounts or applicationId changes
    useEffect(() => {
        if (props.applicationId) {
            variablesContainer.loadApplicationVariablesByApplicationId(props.applicationId);
        }
    }, [props.applicationId]);

    useEffect(() => {
        if (props.organizationId) {
            organizationVariablesContainer.loadOrganizationVariablesByOrganizationId(props.organizationId);
        }
    }, [props.organizationId]);

    const isRecentlyAddedModuleFormField = (field: TemplateFormFieldModel) => {
        return props.recentlyAddedModuleFormFields?.some(f => f.id == field.id);
    };

    const isFieldIncomplete = (field: TemplateFormFieldModel): boolean => {
        if (!field.isRequired) return false;
        const value = getValue(field);
        if (value && value.trim() !== '') return false;
        // No saved value — check whether a conditional default would provide one.
        // If it does, the field is not considered incomplete (the default will be
        // persisted on the next save/navigation).
        const effectiveDefault = getEffectiveDefault(field);
        return !effectiveDefault || effectiveDefault.trim() === '';
    };

    const getValue = (field: TemplateFormFieldModel) => {
        const foundField = props.values?.find(v => (field?.name && v?.dynamicFormFieldName == field?.name) || (field?.id && v?.templateFormFieldId == field?.id));
        // If there is no stored field value record at all, return undefined so
        // getEffectiveDefault / getDisplayValue can fall back to the conditional
        // or static default.  Once a record exists, return its value verbatim
        // (including empty string) so the user can clear a field completely
        // without it snapping back to the default on the last keystroke.
        if (!foundField) return undefined;
        const val = foundField.value;
        // Guard against the "\n" artefact a blank contentEditable div can emit.
        if (val === null || val === undefined || val === '\n') return undefined;
        return val;
    };

    /**
     * Returns the effective default value for a field: evaluates any
     * conditionalDefaults first, then falls back to the static defaultValue.
     * Returns undefined (not empty string) when there is no meaningful default,
     * so that field components do not render a "Reset to default" button for
     * fields that have no default configured.
     */
    const getEffectiveDefault = (field: TemplateFormFieldModel): string | undefined => {
        const allFields = props.allFormFields ?? props.section?.templateFormFields ?? [];
        const result = evaluateConditionalDefault(field, allFields, props.values ?? []);
        return (result !== undefined && result !== '') ? result : undefined;
    };

    /**
     * Returns the value to display for a field.
     *
     * The stored user-supplied value always takes precedence. The effective
     * default (conditional or static) is only used as a fallback when no
     * stored value exists, ensuring fields with conditional defaults remain
     * editable at all times.
     */
    const getDisplayValue = (field: TemplateFormFieldModel): string | undefined => {
        return getValue(field) ?? getEffectiveDefault(field);
    };

    const getFieldName = (field: TemplateFormFieldModel): string => {
        return field.name ?? field.id;
    };

    const handleChange = (field: TemplateFormFieldModel, value: string | boolean) => {
        props.onFieldValueChange(field, value.toString());
    };

    const handleReset = (field: TemplateFormFieldModel) => {
        const effectiveDefault = getEffectiveDefault(field);
        if (effectiveDefault !== undefined) {
            props.onFieldValueChange(field, effectiveDefault);
        }
    };

    // Get variations for a specific field
    const getFieldVariations = (field: TemplateFormFieldModel): ApplicationVariable[] => {
        return filterFieldVariations(variablesContainer.applicationVariables, field.title);
    };

    // Check if field has any saved variations
    const hasFieldVariations = (field: TemplateFormFieldModel): boolean => {
        return getFieldVariations(field).length > 0;
    };

    // Handle opening save variation modal
    const handleOpenSaveVariation = (field: TemplateFormFieldModel) => {
        setSelectedField(field);
        setSaveVariationModalVisible(true);
    };

    // Handle opening view variations modal
    const handleOpenViewVariations = (field: TemplateFormFieldModel) => {
        setSelectedField(field);
        setViewVariationsModalVisible(true);
    };


    // Handle selecting a variation to use
    const handleSelectVariation = (variation: ApplicationVariable) => {
        if (selectedField) {
            props.onFieldValueChange(selectedField, variation.value);
        }
        setViewVariationsModalVisible(false);
        setSelectedField(null);
    };

    const renderCheckbox = (field: TemplateFormFieldModel) => {
        return (
            <div>
                <div className={label}>{field.label}</div>
                <CheckboxField
                    key={field.id ?? field.name}
                    name={`fields.${getFieldName(field)}`}
                    checked={getValue(field) == 'true'}
                    onChange={(e) => handleChange(field, e.target.checked.toString())}
                    className={checkboxField}
                />
            </div>
        )
    };

    const renderSwitch = (field: TemplateFormFieldModel) => {
        return (
            <div>
                <div className={label}>{field.label}</div>
                <Switch
                    className={switchContainer}
                    key={field.id ?? field.name}
                    name={`fields.${getFieldName(field)}`}
                    onChange={(checked) => handleChange(field, checked.toString())}
                    checked={getValue(field) == 'true'}
                    label={field.label}
                />
            </div>
        )
    };

    const renderShortText = (field: TemplateFormFieldModel, type?: string) => {
        if (isDeprecatedSingleDayTimeRangeField(field, props.appContainer, props.appliedAppTemplate)) {
            const siblings = getSingleDayTimeRangeSiblings(field);
            const siblingDayLabels = siblings.map(f => f.title ?? f.label ?? '').filter(Boolean);
            return (
                <SingleDayTimeRangeField
                    key={field.id ?? field.name}
                    name={`fields.${getFieldName(field)}`}
                    label={field.label}
                    value={getFieldDisplayValue(field)}
                    required={field.isRequired}
                    onChange={(value) => handleSingleDayTimeRangeChange(field, value)}
                    onBlur={() => { }}
                    onValidationChange={(hasErrors) => {
                        if (props.onFieldValidationChange) {
                            props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                        }
                    }}
                    siblingDayLabels={siblingDayLabels}
                    onCopyToOtherDays={(targetLabels, valueToApply) => handleCopyToSiblingDays(field, targetLabels, valueToApply)}
                />
            )
        }

        if (isDeprecatedSpecialDateField(field, props.appContainer, props.appliedAppTemplate)) {
            return (
                <SpecialDateField
                    key={field.id ?? field.name}
                    name={`fields.${getFieldName(field)}`}
                    label={field.label}
                    value={getDisplayValue(field)}
                    onChange={(value) => handleChange(field, value)}
                    onBlur={() => { }}
                    onValidationChange={(hasErrors) => {
                        if (props.onFieldValidationChange) {
                            props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                        }
                    }}
                />
            )
        }

        return (
            <TextField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                value={getDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                type={type}
                onChange={(e) => handleChange(field, e.target.value)}
                onBlur={() => { }}
                onReset={() => handleReset(field)}
                label={field.label}
                placeholder={field.placeholder}
            />
        )
    };

    const renderPhoneNumber = (field: TemplateFormFieldModel) => {
        return (
            <PhoneNumberFormField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                value={getDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                onChange={(value) => handleChange(field, value)}
                onReset={() => handleReset(field)}
                label={field.label}
                placeholder={field.placeholder}
                onValidationChange={(hasErrors) => {
                    if (props.onFieldValidationChange) {
                        props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                    }
                }}
            />
        );
    };

    const renderLongText = (field: TemplateFormFieldModel) => {
        return (
            <TextAreaField
                showResponseVariableTest
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                value={getDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                onChange={(e) => handleChange(field, e.target.value)}
                onBlur={() => { }}
                onReset={() => handleReset(field)}
                label={field.label}
                placeholder={field.placeholder}
            />
        )
    };

    const renderSsmlField = (field: TemplateFormFieldModel) => {
        return (
            <SsmlField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                value={getDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                onChange={(value) => handleChange(field, value)}
                onBlur={() => { }}
                onReset={() => handleReset(field)}
                label={field.label}
                placeholder={field.placeholder}
                applicationId={props.applicationId} />
        )
    };

    const renderSelect = (field: TemplateFormFieldModel) => {
        return (
            <SelectField
                key={field.id ?? field.name}
                className={selectField}
                onChange={(e: any) => handleChange(field, e.value)}
                onBlur={() => { }}
                name={`fields.${getFieldName(field)}`}
                value={getDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                onReset={() => handleReset(field)}
                label={field.label}
                placeholder={field.placeholder}
                options={field.options.map(o => ({ value: o.value, label: o.label }))}
            />
        )
    };

    const renderCheckboxGroup = (field: TemplateFormFieldModel) => {
        // note: checkboxes use pipe delimited values from the core value
        return (
            <CheckboxGroup
                key={field.id ?? field.name}
                onChange={(e, c) => {
                    var val = getDisplayValue(field);
                    if (c) {
                        val += `|${e.value}`;
                    } else {
                        val = val.replace(e.value, '');
                    }
                    handleChange(field, val)
                }}
                values={(getDisplayValue(field))?.split('|')}
                options={field.options.map(o => ({ value: o.value, label: o.label }))}
                defaultValue={getEffectiveDefault(field)}
                onReset={() => handleReset(field)}
            />
        )
    };

    const renderRadioGroup = (field: TemplateFormFieldModel) => {
        return (
            <RadioGroup
                key={field.id ?? field.name}
                onChange={(e) => handleChange(field, e.value)}
                value={getDisplayValue(field)}
                options={field.options.map(o => ({ value: o.value, label: o.label }))}
                defaultValue={getEffectiveDefault(field)}
                onReset={() => handleReset(field)}
            />
        )
    };

    const renderFileUpload = (field: TemplateFormFieldModel) => {
        return (
            <Subscribe to={[MediaContainer]}>
                {(mediaContainer: MediaContainer) => (
                    <FileUploadField
                        onChange={(e) => handleChange(field, e.url)}
                        disabled={false}
                        field={field}
                        mediaContainer={mediaContainer}
                        applicationId={props.applicationId}
                        value={getValue(field)}
                        preview={props.preview}
                    />
                )}
            </Subscribe>
        )
    };

    const renderTimeRange = (field: TemplateFormFieldModel) => {
        return (
            <TimeRangeField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                label={field.label}
                value={getDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                onChange={(value) => handleChange(field, value)}
                onBlur={() => { }}
                onReset={() => handleReset(field)}
            />
        )
    };

    const getSingleDayTimeRangeSiblings = (field: TemplateFormFieldModel): TemplateFormFieldModel[] => {
        return (props.section?.templateFormFields ?? []).filter(
            f => f.id !== field.id &&
                (f.fieldType === 'SingleDayTimeRangeField' ||
                    isDeprecatedSingleDayTimeRangeField(f, props.appContainer, props.appliedAppTemplate))
        );
    };

    /**
     * Canonical Mon→Sun variable order used to find the "next day" field.
     * Must match the uppercase values used in SINGLE_DAY_TIME_RANGE_FIELD_ASSOCIATED_VARIABLES
     * in fieldTypeUtils.ts (i.e. the actual associatedVariable values stored by the backend).
     */
    const DAY_VARIABLE_ORDER = [
        '{MONDAY}', '{TUESDAY}', '{WEDNESDAY}', '{THURSDAY}',
        '{FRIDAY}', '{SATURDAY}', '{SUNDAY}',
    ];

    /**
     * Returns the SingleDayTimeRange field that represents the day after the
     * given field, or null if there is none.  Resolution order:
     *   1. Match on associatedVariable using DAY_VARIABLE_ORDER.
     *   2. Fall back to the next field by position in the section.
     */
    const getNextDaySiblingField = (field: TemplateFormFieldModel): TemplateFormFieldModel | null => {
        const allDayFields = [field, ...getSingleDayTimeRangeSiblings(field)];

        // Try variable-based ordering first
        const varIndex = DAY_VARIABLE_ORDER.indexOf(field.associatedVariable);
        if (varIndex !== -1) {
            const nextVar = DAY_VARIABLE_ORDER[(varIndex + 1) % DAY_VARIABLE_ORDER.length];
            const byVar = allDayFields.find(f => f.associatedVariable === nextVar);
            if (byVar) return byVar;
        }

        // Fall back to position in section
        const sectionFields = props.section?.templateFormFields ?? [];
        const posIndex = sectionFields.findIndex(f => f.id === field.id || f.name === field.name);
        if (posIndex !== -1 && posIndex + 1 < sectionFields.length) {
            const candidate = sectionFields[posIndex + 1];
            if (
                candidate.fieldType === 'SingleDayTimeRangeField' ||
                isDeprecatedSingleDayTimeRangeField(candidate, props.appContainer, props.appliedAppTemplate)
            ) {
                return candidate;
            }
        }

        return null;
    };

    /**
     * Returns the SingleDayTimeRange field that represents the day before the
     * given field, or null if there is none.  Mirrors getNextDaySiblingField.
     */
    const getPreviousDaySiblingField = (field: TemplateFormFieldModel): TemplateFormFieldModel | null => {
        const allDayFields = [field, ...getSingleDayTimeRangeSiblings(field)];

        // Try variable-based ordering first
        const varIndex = DAY_VARIABLE_ORDER.indexOf(field.associatedVariable);
        if (varIndex !== -1) {
            const prevVar = DAY_VARIABLE_ORDER[(varIndex - 1 + DAY_VARIABLE_ORDER.length) % DAY_VARIABLE_ORDER.length];
            const byVar = allDayFields.find(f => f.associatedVariable === prevVar);
            if (byVar) return byVar;
        }

        // Fall back to position in section
        const sectionFields = props.section?.templateFormFields ?? [];
        const posIndex = sectionFields.findIndex(f => f.id === field.id || f.name === field.name);
        if (posIndex > 0) {
            const candidate = sectionFields[posIndex - 1];
            if (
                candidate.fieldType === 'SingleDayTimeRangeField' ||
                isDeprecatedSingleDayTimeRangeField(candidate, props.appContainer, props.appliedAppTemplate)
            ) {
                return candidate;
            }
        }

        return null;
    };

    /**
     * Handle a value change from a SingleDayTimeRangeField.
     * If the value contains an overnight range (end < start in 24h), the
     * overnight portion is split off and merged into the next day's field,
     * prepending the midnight-to-end overflow while preserving the next day's
     * own same-day hours.
     *
     * When both the current-day and next-day fields need updating we use
     * onMultipleFieldValuesChange (if provided) so that both writes are
     * applied atomically in a single setFieldValue call — avoiding the
     * stale-closure race where a second synchronous call overwrites the first.
     */
    const handleSingleDayTimeRangeChange = (field: TemplateFormFieldModel, value: string) => {
        const { currentDay, nextDay } = splitOvernightValue(value);

        // Re-apply any overnight prefix from the previous day that was stored in
        // this field.  getFieldDisplayValue strips this prefix before showing it
        // in the UI, so the component returns a clean value — we must put the
        // prefix back when writing to preserve the previous day's overnight
        // display reconstruction.
        const rawStoredValue = getValue(field);
        const prevField = getPreviousDaySiblingField(field);
        const prevFieldRawValue = prevField ? getDisplayValue(prevField) : undefined;
        const { prefix: prevDayPrefix } = extractOvernightPrefix(rawStoredValue, prevFieldRawValue);
        const currentDayValue = prevDayPrefix
            ? mergeOvernightPrefix(prevDayPrefix, currentDay)
            : currentDay;

        const nextField = getNextDaySiblingField(field);
        if (nextDay !== null) {
            if (nextField) {
                const nextFieldCurrentValue = getDisplayValue(nextField);
                const mergedNextValue = mergeOvernightPrefix(nextDay, nextFieldCurrentValue);
                if (props.onMultipleFieldValuesChange) {
                    props.onMultipleFieldValuesChange([
                        { field, value: currentDayValue },
                        { field: nextField, value: mergedNextValue },
                    ]);
                } else {
                    props.onFieldValueChange(field, currentDayValue);
                    props.onFieldValueChange(nextField, mergedNextValue);
                }
                return;
            }
        } else if (nextField) {
            // No overnight overflow any more — strip any previously-written
            // overnight prefix from the next-day field so stale "00:00,HH:MM"
            // periods don't persist across edits.
            const nextFieldRaw = getDisplayValue(nextField);
            const { prefix: stalePrefix, remainder: cleanedNextValue } = extractOvernightPrefix(nextFieldRaw, currentDayValue);
            if (stalePrefix) {
                if (props.onMultipleFieldValuesChange) {
                    props.onMultipleFieldValuesChange([
                        { field, value: currentDayValue },
                        { field: nextField, value: cleanedNextValue },
                    ]);
                } else {
                    props.onFieldValueChange(field, currentDayValue);
                    props.onFieldValueChange(nextField, cleanedNextValue);
                }
                return;
            }
        }
        props.onFieldValueChange(field, currentDayValue);
    };

    /**
     * Returns the value to pass to a SingleDayTimeRangeField for display
     * purposes.  This is a read-time complement to the write-time split/merge:
     *
     *   1. Strip any overnight prefix the CURRENT field received from the
     *      previous day (a "00:00,HH:MM" period where HH:MM ≤ 06:00).
     *   2. Look at the NEXT day field.  If it carries an overnight prefix that
     *      was written by this field's last save, reconstruct the original
     *      overnight value so the "(next day)" badge renders correctly.
     */
    const getFieldDisplayValue = (field: TemplateFormFieldModel): string | undefined => {
        const rawValue = getDisplayValue(field);

        // 1. Strip this field's own overnight prefix (belongs to previous day).
        //    Pass the previous day's raw value so the heuristic only strips the
        //    prefix when the previous day actually has an overnight range (23:59).
        const prevField = getPreviousDaySiblingField(field);
        const prevFieldRaw = prevField ? getDisplayValue(prevField) : undefined;
        const { prefix: ownPrefix, remainder: cleanValue } = extractOvernightPrefix(rawValue, prevFieldRaw);

        // 2. Look at the next day and extract its overnight prefix.
        //    Pass the current field's clean value as context so the heuristic only
        //    recognises the prefix when this field has an overnight range (23:59).
        const nextField = getNextDaySiblingField(field);
        if (nextField) {
            const nextRaw = getDisplayValue(nextField);
            const { prefix: nextPrefix } = extractOvernightPrefix(nextRaw, cleanValue);
            if (nextPrefix && cleanValue && cleanValue.includes('23:59')) {
                return reconstructOvernightDisplay(cleanValue, nextPrefix);
            }
        }

        // Return cleaned value (own overnight prefix stripped if present)
        return cleanValue || undefined;
    };

    const handleCopyToSiblingDays = (sourceField: TemplateFormFieldModel, targetLabels: string[], valueToApply: string) => {
        const siblings = getSingleDayTimeRangeSiblings(sourceField);
        const targets = siblings.filter(f => targetLabels.includes(f.title ?? f.label ?? ''));
        if (targets.length === 0 || !valueToApply) return;

        const { currentDay, nextDay } = splitOvernightValue(valueToApply);
        const allUpdates: Array<{ field: TemplateFormFieldModel; value: string }> = [];

        // Build a set of target field identifiers so we can avoid conflicting updates.
        const targetIds = new Set(targets.map(f => f.id ?? f.name));

        for (const targetField of targets) {
            // When copying overnight hours every target must carry the midnight overflow
            // prefix so that the day before it can reconstruct its overnight display
            // value. Without the prefix embedded here, a subsequent loop iteration
            // for the next contiguous target would overwrite the prefix that was
            // written as a "nextField" update for the previous target.
            const targetValue =
                nextDay !== null
                    ? mergeOvernightPrefix(nextDay, currentDay)
                    : currentDay;

            allUpdates.push({ field: targetField, value: targetValue });

            // Only write the nextField overflow update when the next sibling is NOT
            // itself a copy target — if it is a target, its own loop iteration above
            // already embeds the prefix correctly, and a duplicate update here would
            // overwrite it with a stale getDisplayValue snapshot.
            if (nextDay !== null) {
                const nextField = getNextDaySiblingField(targetField);
                if (nextField && !targetIds.has(nextField.id ?? nextField.name)) {
                    const mergedValue = mergeOvernightPrefix(nextDay, getDisplayValue(nextField));
                    allUpdates.push({ field: nextField, value: mergedValue });
                }
            }
        }

        if (props.onMultipleFieldValuesChange) {
            props.onMultipleFieldValuesChange(allUpdates);
        } else {
            allUpdates.forEach(({ field, value }) => props.onFieldValueChange(field, value));
        }
    };

    const renderSingleDayTimeRange = (field: TemplateFormFieldModel) => {
        const siblings = getSingleDayTimeRangeSiblings(field);
        const siblingDayLabels = siblings.map(f => f.title ?? f.label ?? '').filter(Boolean);
        return (
            <SingleDayTimeRangeField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                label={field.label}
                value={getFieldDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                required={field.isRequired}
                onChange={(value) => handleSingleDayTimeRangeChange(field, value)}
                onBlur={() => { }}
                onReset={() => handleReset(field)}
                onValidationChange={(hasErrors) => {
                    if (props.onFieldValidationChange) {
                        props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                    }
                }}
                siblingDayLabels={siblingDayLabels}
                onCopyToOtherDays={(targetLabels, valueToApply) => handleCopyToSiblingDays(field, targetLabels, valueToApply)}
            />
        )
    };

    const renderSpecialDate = (field: TemplateFormFieldModel) => {
        return (
            <SpecialDateField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                label={field.label}
                value={getDisplayValue(field)}
                defaultValue={getEffectiveDefault(field)}
                onChange={(value) => handleChange(field, value)}
                onBlur={() => { }}
                onReset={() => handleReset(field)}
                onValidationChange={(hasErrors) => {
                    if (props.onFieldValidationChange) {
                        props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                    }
                }}
            />
        )
    };

    // Check if field type supports variations (text-based fields only)
    const supportsVariations = (field: TemplateFormFieldModel): boolean => {
        const textBasedFieldTypes = [
            "ShortTextField",
            "NumberTextField",
            "DateTextField",
            "LongTextField",
            "SSMLField",
            "SSMLTextField"
        ];
        return textBasedFieldTypes.includes(field.fieldType);
    };

    const renderSetupProperty = (field: TemplateFormFieldModel) => {
        switch (field.fieldType) {
            case "ShortTextField":
                return renderShortText(field);
            case "NumberTextField":
                return renderShortText(field, 'number');
            case "DateTextField":
                return renderShortText(field, 'date');
            case "PhoneNumberField":
                return renderPhoneNumber(field);
            case "LongTextField":
                return renderLongText(field);
            case "SwitchField":
                return renderSwitch(field);
            case "SelectField":
                return renderSelect(field);
            case "CheckboxGroupField":
                return renderCheckboxGroup(field);
            case "RadioGroupField":
                return renderRadioGroup(field);
            case "CheckboxField":
                return renderCheckbox(field);
            case "FileUploadField":
                return renderFileUpload(field);
            case "TimeRangeField":
                return renderTimeRange(field);
            case "SingleDayTimeRangeField":
                return renderSingleDayTimeRange(field);
            case "SpecialDateField":
                return renderSpecialDate(field);
            case "SSMLField":
            case "SSMLTextField":
                return renderSsmlField(field);
        }
    };

    const renderFields = () => {
        // Use the consolidated helper function to get visible fields
        // Note: In non-Spark contexts, showAdminFields defaults to true
        const showAdmin = sparkContainer.inSpark ? (props.showAdminFields ?? false) : true;
        const fields = getVisibleFields(
            props.section,
            showAdmin,
            props.values
        );

        return (
            <div className={setupSectionContainer}>
                <h3>{props.section.title}</h3>
                {fields.map((p, subIdx) => {
                    const fieldSupportsVariations = supportsVariations(p);
                    return (
                        <div key={`setupSection_${subIdx}`} className={setupPropertyContainer}>
                            <FormFieldCard
                                title={p.title}
                                tip={p.tip}
                                required={p.isRequired}
                                isIncomplete={isFieldIncomplete(p)}
                                isRecentlyAddedModuleFormField={isRecentlyAddedModuleFormField(p)}
                                hasVariations={fieldSupportsVariations ? hasFieldVariations(p) : false}
                                onSaveVariation={fieldSupportsVariations ? () => handleOpenSaveVariation(p) : undefined}
                                onViewVariations={fieldSupportsVariations ? () => handleOpenViewVariations(p) : undefined}
                            >
                                {renderSetupProperty(p)}
                            </FormFieldCard>
                        </div>
                    )
                })}
            </div>
        )
    };

    return (
        <>
            {props.fieldsOnly && renderFields()}
            <PanelContainer className={panelContainerStyle}>
                <CustomScrollbars autoHide={true} autoHeight={true} autoHeightMin="calc(100vh - 136px)" autoHeightMax="calc(100vh - 136px)">
                    {renderFields()}
                </CustomScrollbars>
                {props.section.instructionsMarkdown &&
                    <ScrollablePanelBody className={previewStyle}>
                        <TemplateInstructionsPanel
                            title="Preview"
                            instructionsMarkdown={props.section.instructionsMarkdown}
                        />
                    </ScrollablePanelBody>
                }
            </PanelContainer>

            {/* Variation Modals */}
            {selectedField && (
                <>
                    <SaveFieldVariationModal
                        isVisible={saveVariationModalVisible}
                        fieldTitle={selectedField.title}
                        fieldValue={getValue(selectedField) || ''}
                        onClose={() => {
                            setSaveVariationModalVisible(false);
                            setSelectedField(null);
                        }}
                        applicationId={props.applicationId}
                    />
                    <ViewFieldVariationsModal
                        isVisible={viewVariationsModalVisible}
                        fieldTitle={selectedField.title}
                        variations={getFieldVariations(selectedField)}
                        onClose={() => {
                            setViewVariationsModalVisible(false);
                            setSelectedField(null);
                        }}
                        onSelectVariation={handleSelectVariation}
                    />
                </>
            )}
        </>
    )
}

const panelContainerStyle = css`
`

const previewStyle = css`
    height: 100%;
`

const selectField = css`
    padding-top: 16px;
`;

const checkboxField = css`
    padding-left: 16px;
    .tooltip {
        margin-top: 3px;
    }
`;

const setupSectionContainer = css`
    padding: 32px;
    h3 {
        margin-bottom: 16px;
        font-style: normal;
        font-weight: normal;
        font-size: 18px;
        line-height: 24px;
    }
`;

const label = css`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: normal;
    text-align: left;
    margin: 8px 16px;
`;
const switchContainer = css`
    margin-bottom: 32px;
`
const setupPropertyContainer = css`
    padding: 16px 0px;
    position: relative;
    .field-triangle {
        position: absolute;
        width: 24px;
        height: 24px;
        background: ${color_shades_lighter};
        right: -44px;
        top: 64px;
        transform: rotate(45deg);
        border-left: 1px solid ${color_shades_dark};
        border-bottom: 1px solid ${color_shades_dark};
    }
`;

export default AppliedApplicationTemplateSectionForm;
